import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { intersection, isEmpty } from 'lodash';
import { useUpdateEffect } from 'react-use';

import SelectField from 'components/Fields/SelectField/SelectField';
import { USE_LABEL_FROM_TRANSLATION_PREFIX } from 'components/ContentReporting/shared-constants';
import {
  Field,
  FieldProperties,
} from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import URIField from 'components/ContentReporting/Form/Fields/URIField';
import ImageChooser from 'components/ContentReporting/Form/Fields/ImageChooser/ImageChooser';
import { ErrorMessage, OnFieldChangeFunction } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import {
  shouldShowTrackHelperText,
  withFullTranslationKey,
} from 'containers/ContentReporting/ContentPolicyReporting/utils/functions';
import { getAvailableUriTypesPerCountry } from 'containers/ContentReporting/ContentPolicyReporting/utils/functions/uri';
import { FieldOnChangeEvent } from 'containers/ContentReporting/ContentLegalReporting/utils/types';
import {
  areUrisValid,
  couldHaveAssociatedContent,
} from 'containers/ContentReporting/ContentLegalReporting/utils/functions';
import {
  ContentTypeAssetTypeMapping,
  couldHaveAssetExistenceCheck,
  getAvailableAssetTypesPerCountry,
} from 'containers/ContentReporting/ContentPolicyReporting/utils/functions/contentType';
import { CONTENT_TYPE_MAPPER } from 'containers/ContentReporting/ContentPolicyReporting/utils/constants/contentType';
import { useIsPlainPage } from 'context/PlainPage/plainPage';
import { ConfigContext } from 'context/Config/ConfigProvider';
import { SUPPORT_EMAIL } from 'lib/shared/config/config';
import { ContentType, contentTypeFromJSON, contentTypeToJSON } from 'proto/generated/tintin_content_policy';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useAssociatedContent } from 'hooks/useAssociatedContent';
import { useEntityType } from 'hooks/useEntityType';
import { useAssetExists } from 'hooks/useAssetExists';
import { UserInfoContext } from 'context/UserInfo/UserInfoProvider';
import { EntityType, entityTypeFromJSON, ResolvedEntityType } from 'proto/generated/inspector_gadget';
import { assetTypeFromJSON, assetTypeToJSON } from 'proto/generated/spotify/catalogmanagement/entity/v1/asset';

import { getContentTypeOptionsForUri } from '../form-utils';
import { getUriType, isSpotifyUri } from '../validation-utils';
import UserCountryField from '../Form/Fields/UserCountryField';

export interface Step2ReportedContentUriAndTypeProps {
  forceValidation: boolean;
  onFieldChange: OnFieldChangeFunction;
  fieldValue: (fieldIdentifier: string) => string | any;
  fieldValid: (fieldIdentifier: string) => boolean;
  setLoading: (_: boolean) => void;
  isEmployee?: boolean;
}

const Step2ReportedContentUriAndType = ({
  forceValidation,
  onFieldChange,
  fieldValue,
  fieldValid,
  isEmployee = false,
}: Step2ReportedContentUriAndTypeProps): JSX.Element => {
  const uriValue = fieldValue(Field.REPORTED_CONTENT_URIS);
  const uriValid = fieldValid(Field.REPORTED_CONTENT_URIS);
  const commonAssetTypeValue = fieldValue(Field.COMMON_ASSET_TYPE);
  const commonAssetTypeValid = fieldValid(Field.COMMON_ASSET_TYPE);
  const assetIdValue = fieldValue(Field.REPORTED_ASSET_ID);
  const entityTypeValue = fieldValue(Field.REPORTED_ENTITY_TYPE);
  const entityTypeValueJSON = entityTypeValue ? entityTypeFromJSON(entityTypeValue) : EntityType.ENTITY_TYPE_UNKNOWN;
  const assetIdValid = fieldValid(Field.REPORTED_ASSET_ID);

  const uriValueMemo = useMemo(() => (!!uriValue ? [uriValue] : []), [uriValue]);
  const uriValidMemo = useMemo(() => uriValid, [uriValid]);

  const commonAssetTypeValueMemo = useMemo(() => commonAssetTypeValue, [commonAssetTypeValue]);
  const commonAssetTypeValidMemo = useMemo(() => commonAssetTypeValid, [commonAssetTypeValid]);

  const assetIdValueMemo = useMemo(() => [assetIdValue], [assetIdValue]);
  const assetIdValidMemo = useMemo(() => assetIdValid, [assetIdValid]);

  const [uriErrorMessage, setUriErrorMessage] = useState('');
  const [contentTypeErrorMessage, setContentTypeErrorMessage] = useState('');

  const { t } = useTranslation('content_policy_reporting');
  const isPlainWebsite = useIsPlainPage();
  const { supportSiteUrl } = useContext(ConfigContext);
  const { userCountryIsoCode } = useContext(UserInfoContext);
  const { isFeatureFlagEnabled } = useFeatureFlags();

  const uriToContentTypeMappings = getAvailableAssetTypesPerCountry({
    isEmployee,
    userCountryIsoCode,
    isFeatureFlagEnabled,
  });

  const contentTypeOptionsForUri = getContentTypeOptionsForUri(
    uriToContentTypeMappings,
    uriValueMemo,
    uriValidMemo,
    entityTypeValue,
  );

  const selectedContentTypeOptionMemo = useMemo(
    () =>
      !!commonAssetTypeValueMemo
        ? contentTypeOptionsForUri?.find(
            (asset: ContentTypeAssetTypeMapping) =>
              asset.commonAssetType === assetTypeFromJSON(commonAssetTypeValueMemo),
          )
        : undefined,
    [contentTypeOptionsForUri, commonAssetTypeValueMemo],
  );
  const selectedContentTypeValueMemo = useMemo(
    () => (!!selectedContentTypeOptionMemo ? contentTypeToJSON(selectedContentTypeOptionMemo.contentType) : undefined),
    [selectedContentTypeOptionMemo],
  );
  const convertedContentTypeValueMemo = useMemo(
    () => (selectedContentTypeValueMemo ? contentTypeFromJSON(selectedContentTypeValueMemo) : undefined),
    [selectedContentTypeValueMemo],
  );

  const commonAssetTypeOptionsMemo = useMemo(
    () =>
      !!entityTypeValue
        ? contentTypeOptionsForUri?.map((asset: ContentTypeAssetTypeMapping) => contentTypeToJSON(asset.contentType))
        : [],
    [contentTypeOptionsForUri, entityTypeValue],
  );

  const { resolvedEntityType } = useEntityType(uriValueMemo);

  const handleEntityType = (uriResolvedEntityType: ResolvedEntityType[]) => {
    const entityType = !!resolvedEntityType.length
      ? uriResolvedEntityType[0].entityType
      : EntityType.ENTITY_TYPE_UNKNOWN;

    onFieldChange({
      fieldIdentifier: Field.REPORTED_ENTITY_TYPE,
      value: entityTypeFromJSON(entityType),
      valid: true,
    });
  };

  const entityExists = !!resolvedEntityType.length ? resolvedEntityType[0].entityExists : true;

  const { fetchAssociatedContent, associatedContentElements } = useAssociatedContent({
    contentType: CONTENT_TYPE_MAPPER[convertedContentTypeValueMemo!],
  });
  const { fetchAssetExists } = useAssetExists();

  const acceptedUriTypes = getAvailableUriTypesPerCountry({
    isEmployee,
    userCountryIsoCode,
    isFeatureFlagEnabled,
  });

  const areUrisValueValid = areUrisValid(acceptedUriTypes, 1);

  const contactURL = isPlainWebsite
    ? `mailto:${SUPPORT_EMAIL}`
    : `${supportSiteUrl('/contact-spotify-support/')}${location.search}`;

  const handleAssociatedContent = async ({
    reportedUriValue,
    reportedAssetId,
  }: {
    reportedUriValue: string;
    reportedAssetId: string;
  }) => {
    const { associatedContent: newAssociatedContent, assetIds: assetIdsAvailable } = await fetchAssociatedContent([
      reportedUriValue,
    ]);

    if ((isEmpty(newAssociatedContent) || isEmpty(assetIdsAvailable)) && !isEmpty(reportedAssetId)) {
      onFieldChange({
        fieldIdentifier: Field.REPORTED_ASSET_ID,
        value: '',
        valid: true,
      });
    } else {
      const [validAssetId = ''] = intersection(reportedAssetId, assetIdsAvailable);
      onFieldChange({
        fieldIdentifier: Field.REPORTED_ASSET_ID,
        value: validAssetId,
        valid: true,
      });
    }
  };

  const getUriErrorMessage = (uri: string): string => {
    if (!isSpotifyUri(uri)) {
      return 'content_uri_invalid';
    }

    if (!areUrisValueValid([uri])) {
      return getUriType(uri) === 'album' ? 'album_uri_type_not_supported' : 'content_uri_not_accepted';
    }

    if (!entityExists) {
      return 'content_non_existent_error';
    }

    return '';
  };

  const isContentTypeFieldValid = async () => {
    let isValid = true;

    const hasAssociatedContentCheck = couldHaveAssociatedContent(
      CONTENT_TYPE_MAPPER[convertedContentTypeValueMemo!],
      entityTypeValueJSON,
    );

    if (hasAssociatedContentCheck) {
      const { associatedContent: newAssociatedContent, assetIds: assetIdsAvailable } = await fetchAssociatedContent(
        uriValueMemo,
      );

      isValid = !!newAssociatedContent.length && !!assetIdsAvailable.length;
    }

    const hasAssetExistsCheck = couldHaveAssetExistenceCheck({
      entityType: entityTypeValueJSON,
      contentType: convertedContentTypeValueMemo,
    });

    if (hasAssetExistsCheck) {
      const doesAssetExists = await fetchAssetExists(
        uriValueMemo[0],
        convertedContentTypeValueMemo,
        entityTypeValueJSON,
      );

      isValid = doesAssetExists;
    }

    onFieldChange({
      fieldIdentifier: Field.COMMON_ASSET_TYPE,
      value: commonAssetTypeValueMemo,
      valid: isValid,
    });

    onFieldChange({
      fieldIdentifier: Field.REPORTED_CONTENT_TYPE,
      value: selectedContentTypeValueMemo,
      valid: isValid,
    });
  };

  const getContentTypeErrorMessage = (hasAssetExistenceCheck: boolean, hasAssociatedContentCheck: boolean): string => {
    if (hasAssetExistenceCheck || hasAssociatedContentCheck) {
      return convertedContentTypeValueMemo === ContentType.CONTENT_TYPE_ARTIST_MERCHANDISE
        ? ErrorMessage.NON_EXISTENT_MERCH_ASSOCIATED_CONTENT
        : ErrorMessage.ASSET_NON_EXISTENT_ERROR;
    }

    return 'field_required';
  };

  useUpdateEffect(() => {
    handleEntityType(resolvedEntityType);
  }, [resolvedEntityType]);

  useUpdateEffect(() => {
    handleAssociatedContent({ reportedUriValue: uriValueMemo[0], reportedAssetId: assetIdValueMemo[0] });
    isContentTypeFieldValid();
    setContentTypeErrorMessage(
      getContentTypeErrorMessage(
        true,
        couldHaveAssociatedContent(CONTENT_TYPE_MAPPER[convertedContentTypeValueMemo!], entityTypeValueJSON),
      ),
    );
  }, [commonAssetTypeValueMemo]);

  const validateUriField = (uris: string[]) => {
    return (
      entityExists &&
      ([EntityType.ENTITY_TYPE_AUDIOBOOK, EntityType.ENTITY_TYPE_AUDIOBOOK_CHAPTER].includes(entityTypeValueJSON) ||
        areUrisValueValid(uris))
    );
  };

  return (
    <>
      <UserCountryField forceValidation={forceValidation} />
      <URIField
        label={t(FieldProperties.productUris.label)}
        maxURIsAmount={1}
        value={uriValueMemo}
        onChange={uris => {
          onFieldChange({
            fieldIdentifier: Field.REPORTED_CONTENT_URIS,
            value: uris[0],
            valid: validateUriField(uris),
          });

          onFieldChange({
            fieldIdentifier: Field.COMMON_ASSET_TYPE,
            value: '',
            valid: true,
          });

          onFieldChange({
            fieldIdentifier: Field.REPORTED_CONTENT_TYPE,
            value: '',
            valid: true,
          });

          setUriErrorMessage(getUriErrorMessage(uris[0]));
        }}
        isFieldValid={validateUriField}
        translationPrefix="content_policy_reporting"
        popoverMessage="please_provide_the_uri_leading_to_the_reporting_content"
        helperMessage={
          shouldShowTrackHelperText(isEmployee, entityTypeValueJSON, uriToContentTypeMappings)
            ? t('track_uri_type_not_supported', { url: contactURL })
            : ''
        }
        isValidUriMessage={t(uriErrorMessage, { url: contactURL })}
        key={`${uriValue}-${entityExists}-${entityTypeValue}`}
      />

      <SelectField
        field={{
          name: Field.COMMON_ASSET_TYPE,
          type: 'text',
          options: commonAssetTypeOptionsMemo,
          required: true,
          labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
        }}
        hasDefaultPlaceholder
        value={selectedContentTypeValueMemo}
        valid={commonAssetTypeValidMemo}
        errorMessageKey={t(contentTypeErrorMessage)}
        tPrefix={withFullTranslationKey(FieldProperties[Field.COMMON_ASSET_TYPE].translationPrefix)}
        onFieldChange={(fieldChange: FieldOnChangeEvent) => {
          if (!isEmpty(fieldChange.value)) {
            const selectedContentType = contentTypeFromJSON(fieldChange.value);
            const selectedOption = contentTypeOptionsForUri?.find(
              (asset: ContentTypeAssetTypeMapping) => asset.contentType === selectedContentType,
            );

            onFieldChange({
              fieldIdentifier: Field.COMMON_ASSET_TYPE,
              value: assetTypeToJSON(selectedOption?.commonAssetType),
              valid: true,
            });

            onFieldChange({
              fieldIdentifier: Field.REPORTED_CONTENT_TYPE,
              value: selectedContentType,
              valid: true,
            });
          }
        }}
        forceValidation={forceValidation}
        validateOnChange
      />

      {!isEmpty(associatedContentElements) && (
        <ImageChooser
          field={{ name: Field.REPORTED_ASSET_ID }}
          valid={assetIdValidMemo}
          onFieldChange={(fieldChange: FieldOnChangeEvent) => {
            if (!isEmpty(fieldChange.value)) {
              const [selectedImageId, ...otherSelectedImages] = fieldChange.value;
              onFieldChange({
                fieldIdentifier: fieldChange.fieldIdentifier,
                value: selectedImageId,
                valid: !otherSelectedImages.length && fieldChange.valid,
              });
            }
          }}
          value={[...assetIdValueMemo]}
          images={associatedContentElements}
          label={t('image_chooser_title')}
          translations={{
            errorMessage: t('image_invalid_generic_dynamic_max_number', { max_number: 1 }),
            disclaimer: t('form_associated_content_disclaimer'),
            blurWarning: t('image_unblur_warning'),
            pagination: t('pagination_of'),
          }}
          max={1}
          imageProperty="value"
        />
      )}
    </>
  );
};

export default Step2ReportedContentUriAndType;
